import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { xcg, cwPrettyDate } from '../lib/format';
import { StatusBadge } from '../components/StatusBadge';
import { BookingDetailDrawer } from '../components/BookingDetailDrawer';
import { Search, Loader2 } from 'lucide-react';

const STATUSES = ['All', 'Held', 'Confirmed', 'Cancelled-Released'];

export default function Register() {
  const [bookings, setBookings] = useState(null);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('All');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openBn, setOpenBn] = useState(null);
  const [sp, setSp] = useSearchParams();

  useEffect(() => { api.get('/bookings').then(r => setBookings(r.data)); }, []);

  // Deep-link: /register?bn=BK-... auto-opens the drawer for that booking
  useEffect(() => {
    const bn = sp.get('bn');
    if (bn) setOpenBn(bn);
  }, [sp]);

  const closeDrawer = () => {
    setOpenBn(null);
    if (sp.get('bn')) {
      sp.delete('bn');
      setSp(sp, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    if (!bookings) return [];
    const query = q.trim().toLowerCase();
    return bookings.filter(b => {
      if (query && !(b.Name.toLowerCase().includes(query) || b.Email?.toLowerCase().includes(query) || b['Booking#'].toLowerCase().includes(query))) return false;
      if (status !== 'All' && b.Status !== status) return false;
      if (from && b.CheckIn < from) return false;
      if (to && b.CheckIn > to) return false;
      return true;
    }).sort((a, b) => b.CreatedAt.localeCompare(a.CreatedAt));
  }, [bookings, q, status, from, to]);

  if (!bookings) return <div className="flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Loading…</div>;

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Register</h1>
        <p className="text-[#333]/70 mt-1">Every booking, searchable.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4 mb-4">
        <div className="sm:col-span-2 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#333]/50" />
          <input
            data-testid="register-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name, email, or booking number"
            className="w-full pl-9 pr-4 py-3 min-h-[3rem] bg-white border border-[#CFD8CF] rounded-lg focus:ring-2 focus:ring-[#2E7D32] outline-none"
          />
        </div>
        <select
          data-testid="register-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-white border border-[#CFD8CF] rounded-lg px-4 py-3 min-h-[3rem]"
        >
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex gap-2">
          <input type="date" value={from} onChange={(e)=>setFrom(e.target.value)} className="flex-1 bg-white border border-[#CFD8CF] rounded-lg px-3 py-3 min-h-[3rem]" />
          <input type="date" value={to} onChange={(e)=>setTo(e.target.value)} className="flex-1 bg-white border border-[#CFD8CF] rounded-lg px-3 py-3 min-h-[3rem]" />
        </div>
      </div>

      <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#E8F5E9]/60 text-[#1F5A24]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Booking</th>
                <th className="text-left px-4 py-3 font-medium">Guest</th>
                <th className="text-left px-4 py-3 font-medium">Room</th>
                <th className="text-left px-4 py-3 font-medium">Check in</th>
                <th className="text-left px-4 py-3 font-medium">Amount</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Payment</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr
                  key={b['Booking#']}
                  data-testid={`row-${b['Booking#']}`}
                  onClick={() => setOpenBn(b['Booking#'])}
                  className="border-t border-[#CFD8CF] cursor-pointer hover:bg-[#E8F5E9]/40"
                >
                  <td className="px-4 py-3 font-mono text-xs">{b['Booking#']}</td>
                  <td className="px-4 py-3">{b.Name}</td>
                  <td className="px-4 py-3">{b.RoomType}</td>
                  <td className="px-4 py-3">{cwPrettyDate(b.CheckIn)}</td>
                  <td className="px-4 py-3">{xcg(b.Amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={b.Status} checkedIn={!!b.CheckedInAt && b.Status === 'Confirmed'} /></td>
                  <td className="px-4 py-3"><StatusBadge status={b.PaymentStatus} /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center px-4 py-10 text-[#333]/60">No bookings match those filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openBn && <BookingDetailDrawer bookingNumber={openBn} onClose={closeDrawer} />}
    </div>
  );
}
